import type { ImportFinding, ImportFindingKind } from '@ni/shared';
import { Checkbox } from '../../components/ui/checkbox';
import {
  IMPORT_FINDINGS_HEAD,
  IMPORT_FINDINGS_HINT,
  IMPORT_FINDINGS_PLAN_HINT,
  IMPORT_FINDING_CONFIRM,
  IMPORT_FINDING_WHERE,
} from '../../lib/copy';

/**
 * **What the dry run found about the file as a whole** (import note §4.3), drawn
 * the same way on the two tabs that show it.
 *
 * It lived on the Import tab alone, which is where the confirmation is — and
 * that put the warning and its repair two screens apart: the sentence saying the
 * names are probably coming from the wrong column was on the tab with the
 * button, and the column is chosen on the tab with the mapping. Both tabs draw
 * it now, from one component, so the sentence and the rows it quotes read
 * identically in both places and neither can drift into its own wording.
 *
 * **The tick stays on the Import tab.** Confirming a finding is a decision about
 * *this commit* — the same kind of thing `read_notes` is — and a box on the Plan
 * tab would be a commit decision taken on a screen with no commit on it. The
 * Plan tab gets a pointer instead: where on that page the finding is answered.
 */

/**
 * **One finding's own words**: the server's sentence, and the rows it quotes.
 *
 * The sentence carries this file's numbers ("143 of 176 rows share the name …")
 * and the numbers are the whole of the argument, so it is rendered as it arrives
 * and nothing is composed here. The examples are cells out of somebody else's
 * upload, and are text — never markup — as everything off a file is.
 */
function FindingLines({ finding }: { finding: ImportFinding }) {
  return (
    <>
      <div>{finding.line}</div>
      {finding.examples.length > 0 ? (
        <ul className="fact-lines">
          {finding.examples.map((example) => (
            <li key={example}>{example}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/**
 * **The three questions about the file, above the button they hold shut**
 * (§4.3) — the Import tab's copy.
 *
 * **Each finding carries its own box** rather than one box under the list. Two
 * findings are two different statements about the file and the office may well
 * agree with one and not the other; a single tick would record that they had
 * read "them", which is the claim the trail is least able to stand behind.
 *
 * The id is what the button's `aria-describedby` points at, so the reason the
 * Import button is off is tied to the block that holds it off.
 */
export function FindingsBlock({
  findings,
  confirmed,
  disabled,
  onToggle,
}: {
  findings: readonly ImportFinding[];
  confirmed: readonly ImportFindingKind[];
  disabled: boolean;
  onToggle: (kind: ImportFindingKind, checked: boolean) => void;
}) {
  return (
    <div className="src import-findings" id="import-findings">
      <div className="head">{IMPORT_FINDINGS_HEAD}</div>
      <div>{IMPORT_FINDINGS_HINT}</div>
      {findings.map((finding) => (
        <div key={finding.kind} className="finding">
          <FindingLines finding={finding} />
          <Checkbox
            checked={confirmed.includes(finding.kind)}
            disabled={disabled}
            onChange={(checked) => onToggle(finding.kind, checked)}
          >
            {IMPORT_FINDING_CONFIRM}
          </Checkbox>
        </div>
      ))}
    </div>
  );
}

/**
 * **The same findings on the Plan tab, read only** (2026-09-14).
 *
 * Same heading, same sentences, same quoted rows; in place of the box, one line
 * saying where on this page the finding is answered — the name column for the
 * two about names, the column targets for the one about rows that write nothing.
 *
 * **Nothing is recomputed here.** The list is the session's, published by the
 * server only for the plan version it was run against; a plan saved from this
 * tab invalidates the session read and the block goes with it, which is exactly
 * what should happen to a statement about a mapping that no longer exists.
 *
 * Renders nothing where the dry run found nothing, and on a session that has not
 * had one — `findings` is an empty list in both, and a heading over no findings
 * would read as a fourth thing to answer.
 */
export function PlanFindings({ findings }: { findings: readonly ImportFinding[] }) {
  if (findings.length === 0) return null;

  return (
    <div className="src import-findings plan-findings">
      <div className="head">{IMPORT_FINDINGS_HEAD}</div>
      <div>{IMPORT_FINDINGS_PLAN_HINT}</div>
      {findings.map((finding) => (
        <div key={finding.kind} className="finding">
          <FindingLines finding={finding} />
          <div className="finding-where">{IMPORT_FINDING_WHERE[finding.kind]}</div>
        </div>
      ))}
    </div>
  );
}
